/**
 * Client-only state for the economic outlook page.
 *
 * Zustand is split from TanStack Query per the migration plan: anything that
 * lives on the server (country config, nowcast series, releases) goes through
 * Query; UI state (selected country, active timeframe, open modal, view-mode
 * toggle, view-selector slot) lives here.
 */
import { create } from 'zustand';
import type {
  CountryIso,
  GdpViewSelector,
  InflationType,
  Timeframe,
  ViewMode,
} from './types';

export interface ModalState {
  /** ISO date string (yyyy-mm-dd) the user clicked on the chart. */
  date: string;
  country: CountryIso;
  /** Optional median value at the clicked point — used to show "Current Estimate". */
  value?: number | null;
}

interface NowcastsState {
  selectedCountry: CountryIso;
  /**
   * Active timeframe for the page. Resolves to `gdpTimeframe` in GDP view and
   * `inflationTimeframe` in Inflation view. GDP defaults to `2y`, Inflation
   * to `5y`.
   */
  gdpTimeframe: Timeframe;
  inflationTimeframe: Timeframe;
  modal: ModalState | null;
  /** Activity vs Inflation page-level toggle (right of the title). */
  view: ViewMode;
  /** GDP-view "View:" selector — Nowcast (fan chart) vs Recession (probabilities). */
  gdpViewSelector: GdpViewSelector;
  /** Inflation-view "Type:" selector — Core vs Headline. */
  inflationType: InflationType;

  setSelectedCountry: (country: CountryIso) => void;
  /** Sets the timeframe for whichever view is currently active. */
  setTimeframe: (timeframe: Timeframe) => void;
  openModal: (modal: ModalState) => void;
  closeModal: () => void;
  setView: (view: ViewMode) => void;
  setGdpViewSelector: (selector: GdpViewSelector) => void;
  setInflationType: (type: InflationType) => void;
}

export const useNowcastsStore = create<NowcastsState>((set) => ({
  selectedCountry: 'US',
  gdpTimeframe: '2y',
  inflationTimeframe: '5y',
  modal: null,
  view: 'gdp',
  gdpViewSelector: 'Nowcast',
  inflationType: 'Core',
  setSelectedCountry: (selectedCountry) => set({ selectedCountry }),
  setTimeframe: (timeframe) =>
    set((s) => (s.view === 'inflation' ? { inflationTimeframe: timeframe } : { gdpTimeframe: timeframe })),
  openModal: (modal) => set({ modal }),
  closeModal: () => set({ modal: null }),
  setView: (view) => set({ view }),
  setGdpViewSelector: (gdpViewSelector) => set({ gdpViewSelector }),
  setInflationType: (inflationType) => set({ inflationType }),
}));

/** Convenience selector — returns whichever timeframe matches the current view. */
export function selectActiveTimeframe(s: NowcastsState): Timeframe {
  return s.view === 'inflation' ? s.inflationTimeframe : s.gdpTimeframe;
}
